import { Box, Theme, Typography, useTheme } from '@mui/material';
import { BlankModal } from 'sharedComponents/Modal';
import { styled } from '@mui/material/styles';
import modalImage from './../../../../../assets/connect/match-quiz-modal-image.png';
import { PrimaryButton, TextButton } from 'sharedComponents/Buttons';
import { handleOpenConnectMatchQuiz } from 'features/Connect/utils/connectUtils';
import { ConnectMatchQuizAccessModalDataTestIds } from 'data-testids/ConnectDataTestIds';
import { useStateParam } from 'features/Connect/features/Context/ConnectStateCodeContextProvider';
import auth from 'utils/auth';

interface MatchQuizAccessModalProps {
  open: boolean;
  userId: number;
  onClose: () => void;
}

export const MatchQuizAccessModal = ({ open, onClose, userId }: MatchQuizAccessModalProps) => {
  const theme = useTheme();
  const jobboardState = useStateParam();
  const user = auth.getUser();
  const userLocation = user.preference.geocoded_location;

  const handleIgnore = () => {
    onClose();
  };

  const modalHeader = (
    <HeaderContainer>
      <HeaderImage src={modalImage} alt="Match Quiz Access Modal" />
    </HeaderContainer>
  );

  const modalBody = (
    <BodyContainer>
      <TextContainer>
        <ModalTitle>Want personalized matches?</ModalTitle>
        <ModalDescription>
          Take our 5 min quiz to get the best matches and supercharge your job search! 🚀
        </ModalDescription>
      </TextContainer>
      <ButtonContainer>
        <PrimaryButton
          onClick={() => handleOpenConnectMatchQuiz(userId, jobboardState, userLocation)}
          size="large"
          sx={MatchQuizButton(theme)}
          dataTestId={ConnectMatchQuizAccessModalDataTestIds.TAKE_QUIZ_BUTTON}
        >
          Take Your Match Quiz!
        </PrimaryButton>
        <TextButton
          onClick={handleIgnore}
          sx={IgnoreButton(theme)}
          dataTestId={ConnectMatchQuizAccessModalDataTestIds.IGNORE_BUTTON}
        >
          Not right now
        </TextButton>
      </ButtonContainer>
    </BodyContainer>
  );

  return (
    <BlankModal
      dataTestId={ConnectMatchQuizAccessModalDataTestIds.MODAL}
      header={modalHeader}
      body={modalBody}
      open={open}
      onClose={onClose}
      sx={ModalStyles(theme)}
    />
  );
};

// Styled Components
const HeaderContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
}));

const HeaderImage = styled('img')(({ theme }) => ({
  width: '180px',
  height: 'auto',
  display: 'block',
  margin: `0 auto ${theme.spacing(2)}`,
}));

const BodyContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: theme.spacing(4),
}));

const TextContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: theme.spacing(1),
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: theme.spacing(2),
}));

const ModalTitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMediumBold,
  fontSize: '24px',
  color: theme.palette.black.dark,
  lineHeight: '30px',
}));

const ModalDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textAlign: 'center',
  fontSize: theme.typography.heading7.fontSize,
  fontWeight: theme.typography.fontWeightLight,
  lineHeight: '26px',
}));

const MatchQuizButton = (theme: Theme) => ({
  background: `linear-gradient(90deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.purple.dark} 100%)`,
  borderRadius: theme.shape.borderRadius * 0.5,
  width: '100%',
  fontWeight: 600,
  fontSize: theme.typography.heading7.fontSize,
  color: '#fff',
  textTransform: 'none',
  '&:hover': {
    background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.purple.main} 100%)`,
  },
});

const IgnoreButton = (theme: Theme) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightLight,
  fontSize: theme.typography.subtitle.fontSize,
  color: theme.palette.text.tertiary,
  fontStyle: 'normal',
  lineHeight: '26px',
});

const ModalStyles = (theme) => ({
  width: '100%',
  maxWidth: '447px',
  borderRadius: theme.shape.borderRadius * 1,
  alignSelf: 'center',
  justifySelf: 'center',
  padding: theme.spacing(6, 4, 4),

  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
  },
});
